import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import PlanSelectionModal from '../components/chat/sidebar/PlanSelectionModal';
import { useCompany } from '../contexts/CompanyContext';
import { isV3PlanOverview } from '../utils/planOverview';

/** Where the single personal plan lives under billing v3. */
export const PLAN_BILLING_PATH = '/settings/plan-and-topups';

interface PlanModalContextType {
  openPlanModal: (currentPriceId?: string) => void;
  closePlanModal: () => void;
  isPlanModalOpen: boolean;
}

const PlanModalContext = createContext<PlanModalContextType | null>(null);

interface PlanModalProviderProps {
  children: React.ReactNode;
}

export const PlanModalProvider: React.FC<PlanModalProviderProps> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const currentPriceIdRef = useRef<string | undefined>(undefined);
  const navigate = useNavigate();
  const { planOverview } = useCompany();
  const isV3 = isV3PlanOverview(planOverview);

  // Billing v3 has one personal plan, so the tiered picker is retired and
  // every "choose a plan" entry point lands on Plan & billing instead.
  const openPlanModal = useCallback(
    (currentPriceId?: string) => {
      if (isV3) {
        navigate(PLAN_BILLING_PATH);
        return;
      }
      currentPriceIdRef.current = currentPriceId;
      setIsOpen(true);
    },
    [isV3, navigate],
  );

  const closePlanModal = useCallback(() => {
    setIsOpen(false);
    currentPriceIdRef.current = undefined;
  }, []);

  const contextValue = useMemo<PlanModalContextType>(
    () => ({ openPlanModal, closePlanModal, isPlanModalOpen: isOpen }),
    [openPlanModal, closePlanModal, isOpen],
  );

  return (
    <PlanModalContext.Provider value={contextValue}>
      {children}
      <PlanSelectionModal
        isOpen={isOpen}
        onClose={closePlanModal}
        currentPriceId={currentPriceIdRef.current}
      />
    </PlanModalContext.Provider>
  );
};

export const usePlanModal = (): PlanModalContextType => {
  const context = useContext(PlanModalContext);
  if (!context) {
    throw new Error('usePlanModal must be used within PlanModalProvider');
  }
  return context;
};
